'use client';

import { m } from 'framer-motion';
import { useCallback } from 'react';

import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';

import { varHover } from 'src/components/animate';
import { CustomPopover, usePopover } from 'src/components/custom-popover';
import FlagUzSvg from 'icons/colored/flag-uz.svg';
import FlagRuSvg from 'icons/colored/flag-ru.svg';
import FlagGbSvg from 'icons/colored/flag-gb.svg';
import FlagDeSvg from 'icons/colored/flag-de.svg';
import { useTranslation } from 'src/hooks/use-translation';
import { Language } from 'src/i18n';

// ----------------------------------------------------------------------

const LANGUAGES = {
  uz: {
    label: `O'zbek`,
    value: 'uz',
    icon: FlagUzSvg,
  },
  en: {
    label: 'English',
    value: 'en',
    icon: FlagGbSvg,
  },
  ru: {
    label: 'Русский',
    value: 'ru',
    icon: FlagRuSvg,
  },
  de: {
    label: 'Deutsch',
    value: 'de',
    icon: FlagDeSvg,
  },
} as const;

export function LanguagePopover() {
  const popover = usePopover();
  const { t, i18n } = useTranslation();

  const languageObj = LANGUAGES[i18n.language as Language];

  const handleChangeLang = useCallback(
    (value: Language) => {
      i18n.changeLanguage(value);
      popover.onClose();
    },
    [popover, i18n]
  );

  return (
    <>
      <IconButton
        data-testid="header-language"
        aria-label={t('header.language')}
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          ...(popover.open && {
            bgcolor: 'action.selected',
          }),
        }}
      >
        <Box component={languageObj.icon} sx={{ borderRadius: '4px', width: 28 }} />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 160 }}>
        {Object.values(LANGUAGES).map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === languageObj.value}
            aria-current={option.value === languageObj.value ? 'true' : undefined}
            onClick={() => handleChangeLang(option.value)}
          >
            <Box component={option.icon} sx={{ borderRadius: '4px', width: 28 }} />

            {option.label}
          </MenuItem>
        ))}
      </CustomPopover>
    </>
  );
}
